import React, { createContext, useState, useContext, useEffect } from 'react';
import * as authService from '../services/authService';
import * as userService from '../services/userService';
import { clearTransactionCache } from '../services/paymentService';

// Create the context
const AuthContext = createContext(null);

/**
 * Authentication provider component that wraps your app
 * @param {object} props - Component props
 * @returns {JSX.Element} Authentication provider
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [account, setAccount] = useState(null);
  const [unit, setUnit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [tokenRefreshInProgress, setTokenRefreshInProgress] = useState(false);

  // Initialize authentication state when component mounts
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check if the user is authenticated using the synchronous helper
        if (authService.isAuthenticated()) {
          // Set up axios interceptors for token handling
          const removeInterceptors = authService.setupAxiosInterceptors(async (needsRefresh) => {
            if (needsRefresh) {
              // If the interceptor indicates we should try to refresh first
              setTokenRefreshInProgress(true);
              const refreshSuccess = await authService.refreshAccessToken();
              setTokenRefreshInProgress(false);
              
              // If refresh failed, log out the user
              if (!refreshSuccess) {
                setUser(null);
                setAccount(null);
                setUnit(null);
                setError('Your session has expired. Please login again.');
              }
              return refreshSuccess;
            } else {
              // Just log out directly
              setUser(null);
              setAccount(null);
              setUnit(null);
              setError('Your session has expired. Please login again.');
              return false;
            }
          });
          
          // Get user profile from server (this will check cookie auth server-side)
          const profile = await authService.getUserProfile();
          if (profile) {
            setUser(profile);
            
            // For regular users, we need to have siteName and unitId to get account info
            // The user will need to provide these details or they will be stored in the profile
            if (profile && 
                !profile.roles?.some(role => ['ADMIN', 'MANAGEMENT', 'OPERATOR'].includes(role)) &&
                profile.siteName && 
                profile.unitId) {
              
              try {
                // Get account info using site name and unit id
                // Pass any available UUIDs to avoid redundant lookups
                const accountInfo = await userService.getAccountByUnit(
                  profile.siteName,
                  profile.unitId,
                  profile.siteUuid,
                  profile.unitUuid
                );
                
                if (accountInfo) {
                  setAccount(accountInfo);
                  
                  // Store the unit UUID in the user profile if it was found
                  if (accountInfo.unitUuid) {
                    setUser(prev => ({
                      ...prev,
                      unitUuid: accountInfo.unitUuid
                    }));
                  }
                  
                  // Try to get more detailed unit information for prepaid status
                  try {
                    const unitDetails = await userService.getUnitByNameAndId(
                      profile.siteName,
                      profile.unitId,
                      profile.siteUuid
                    );
                    
                    if (unitDetails) {
                      // console.log('AuthContext - Found unit details:', unitDetails);
                      // Set complete unit data including isPrepaid status
                      setUnit({
                        siteName: profile.siteName,
                        unitId: profile.unitId,
                        unitUuid: accountInfo.unitUuid,
                        siteUuid: profile.siteUuid || accountInfo.siteUuid,
                        isPrepaid: unitDetails.isPrepaid
                      });
                      
                      // Debug log the unit data being set
                      // console.log('AuthContext - Setting unit data with isPrepaid:', unitDetails.isPrepaid);
                    } else {
                      // Fallback to basic unit data
                      setUnit({
                        siteName: profile.siteName,
                        unitId: profile.unitId,
                        unitUuid: accountInfo.unitUuid,
                        siteUuid: profile.siteUuid || accountInfo.siteUuid
                      });
                    }
                  } catch (unitError) {
                    // console.warn('Failed to get detailed unit info:', unitError);
                    // Fallback to basic unit data
                    setUnit({
                      siteName: profile.siteName,
                      unitId: profile.unitId,
                      unitUuid: accountInfo.unitUuid,
                      siteUuid: profile.siteUuid || accountInfo.siteUuid
                    });
                  }
                }
              } catch (err) {
                // console.log("Note: Account info unavailable, continuing with authentication");
              }
            }
          }
          
          return () => {
            // Clean up interceptors when component unmounts
            if (removeInterceptors) removeInterceptors();
          };
        }
      } catch (err) {
        // console.error('Failed to load user profile:', err);
        setError('Session expired. Please login again.');
        // Clear any stored tokens since authentication failed
        authService.logout();
      } finally {
        // Always clear loading state, even if authentication fails
        setLoading(false);
        setIsAuthChecked(true);
      }
    };
    
    initializeAuth();
  }, []);

  /**
   * Authenticate a user with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} Login result object with success, status, and message properties
   */
  const login = async (email, password) => {
    setError(null);
    setLoading(true);
    
    try {
      // console.log('AuthContext: Attempting login...');
      // authService.login now returns the structured response { success, status, message, token?, user? }
      // OR throws an error with structured data { success, status, message } in error.data
      const result = await authService.login(email, password); 
      
      // console.log('AuthContext: Received result from authService:', result);

      // Check the status field from the structured response
      if (result && result.status === 'ACTIVE' && result.success === true) {
        // console.log('AuthContext: Login successful (ACTIVE), setting user profile');
        
        // Wipe stale transaction cache so the dashboard fetches fresh data
        clearTransactionCache(result.user?.accountUuid);
        
        // Extract user and token data (assuming HttpOnly cookies handle token storage)
        const userData = result.user; 
        // const tokenData = result.token; // Available if needed, but likely managed by cookies

        if (!userData) {
          throw new Error('User data missing in successful login response.');
        }
        
        // Store user data in context state and local storage
        setUser(userData);
        authService.setStoredUserInfo(userData); 
        
        // --- Fetch Account and Unit Info (Similar to existing logic) ---
        if (userData && 
            !userData.roles?.some(role => ['ADMIN', 'MANAGEMENT', 'OPERATOR'].includes(role)) &&
            userData.siteName && 
            userData.unitId) {
          
          try {
            const accountInfo = await userService.getAccountByUnit(
              userData.siteName, userData.unitId, userData.siteUuid, userData.unitUuid
            );
            if (accountInfo) {
              setAccount(accountInfo);
              if (accountInfo.unitUuid) {
                // Update user state non-destructively
                setUser(prev => ({ ...prev, unitUuid: accountInfo.unitUuid }));
                // Also update local storage
                authService.setStoredUserInfo({ ...userData, unitUuid: accountInfo.unitUuid });
              }
              // Try getting detailed unit info
              try {
                 const unitDetails = await userService.getUnitByNameAndId(
                   userData.siteName, userData.unitId, userData.siteUuid
                 );
                 if (unitDetails) {
                   // console.log('Login - Found unit details:', unitDetails);
                   setUnit({
                     siteName: userData.siteName, unitId: userData.unitId,
                     unitUuid: accountInfo.unitUuid, siteUuid: userData.siteUuid || accountInfo.siteUuid,
                     isPrepaid: unitDetails.isPrepaid
                   });
                   // console.log('Login - Setting unit data with isPrepaid:', unitDetails.isPrepaid);
                 } else {
                   setUnit({ // Fallback
                     siteName: userData.siteName, unitId: userData.unitId,
                     unitUuid: accountInfo.unitUuid, siteUuid: userData.siteUuid || accountInfo.siteUuid
                   });
                 }
              } catch (unitError) {
                 // console.warn('Failed to get detailed unit info during login:', unitError);
                 setUnit({ // Fallback
                   siteName: userData.siteName, unitId: userData.unitId,
                   unitUuid: accountInfo.unitUuid, siteUuid: userData.siteUuid || accountInfo.siteUuid
                 });
              }
            }
          } catch (err) {
            // console.log("Note: Account info unavailable, continuing with authentication");
          }
        }
        // --- End Fetch Account and Unit Info ---
        
        setLoading(false);
        return { success: true, status: 'ACTIVE' }; // Indicate success to Login.jsx

      } else if (result && result.status === 'PENDING') {
        // Handle PENDING status - User authenticated but account not approved
        // console.log('AuthContext: User login is PENDING');
        setUser(null); // Ensure no user is set
        setAccount(null);
        setUnit(null);
        authService.setStoredUserInfo(null); // Clear any stale user info
        setLoading(false);
        // Return pending status and message to Login.jsx
        return { success: false, status: 'PENDING', message: result.message || 'Account approval is pending.' };

      } else {
        // Handle other non-ACTIVE statuses or unexpected successful responses without ACTIVE status
        // console.warn('AuthContext: Login attempt resulted in non-ACTIVE status or unexpected response:', result);
        const errorMessage = result?.message || 'Login failed. Unexpected status.';
        setError(errorMessage);
        setUser(null);
        setAccount(null);
        setUnit(null);
        authService.setStoredUserInfo(null);
        setLoading(false);
        return { success: false, status: result?.status || 'UNKNOWN', message: errorMessage };
      }
      
    } catch (err) {
      // console.error('AuthContext: Login failed:', err);
      
      // Extract message from structured error data if available (from authService)
      const errorMessage = err?.data?.message || err?.message || 'An unexpected login error occurred.';
      
      setError(errorMessage);
      setUser(null); // Clear user state on error
      setAccount(null);
      setUnit(null);
      authService.setStoredUserInfo(null); // Clear local storage on error
      setLoading(false);
      
      // Return structured error information to Login.jsx
      // Use status from error data if present, otherwise map HTTP status or default
      let status = 'ERROR';
      if (err?.data?.status) {
        status = err.data.status; // e.g., FAILED, BAD_REQUEST
      } else if (err?.response?.status === 401) {
        status = 'FAILED'; // Map 401 to FAILED if no specific status provided
      } else if (err?.response?.status === 400) {
         status = 'BAD_REQUEST'; // Map 400
      } else if (err?.response?.status === 500) {
         status = 'SERVER_ERROR'; // Indicate server error
      }
      
      return { success: false, status: status, message: errorMessage };
    }
  };

  /**
   * Register a new user
   * @param {object} userData - User registration data
   * @returns {Promise<object>} Registration result
   */
  const register = async (userData) => {
    setError(null);
    setLoading(true);
    
    try {
      const result = await authService.register(userData);
      setLoading(false);
      return result;
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Registration failed.');
      setLoading(false);
      throw err;
    }
  };

  /**
   * Register a new user internally
   * @param {object} userData - User registration data
   * @returns {Promise<object>} Registration result
   */
  const registerInternal = async (userData) => {
    setLoading(true);
    setError(null);
    try {
      // console.log('AuthContext: Calling registerInternalUser service');
      // Call the internal registration service
      const responseData = await authService.registerInternalUser(userData);
      // console.log('AuthContext: Internal registration service success:', responseData);
      
      // This flow likely doesn't log the user in or set tokens,
      // it just creates the account (pending approval).
      // We might not need to update authUser state here.
      
      // Return the response data (e.g., the created user info)
      // so the calling component can use it (e.g., for navigation state)
      return responseData; 
    } catch (error) {
      // console.error('AuthContext: Internal registration error:', error);
      // Extract user-friendly message if available from the error response
      const userMessage = error.response?.data?.userMessage || 
                        error.response?.data?.message || 
                        error.message || 
                        'Internal registration failed. Please try again.';
      setError(userMessage);
      throw error; // Re-throw for the component
    } finally {
      setLoading(false);
    }
  };

  /**
   * Log out the current user
   */
  const logout = () => {
    // console.log('AuthContext: Logging out...');
    try {
      // Call the service which handles server-side logout and clears cookies
      authService.logout(); 
      
      // Clear local context state
      setUser(null);
      setAccount(null);
      setUnit(null);
      setError(null);
      authService.setStoredUserInfo(null); // Ensure local storage is cleared too
      // console.log('AuthContext: Logout successful');
    } catch (error) {
      // console.error('AuthContext: Error during logout:', error);
      // Still clear local state even if server call fails
      setUser(null);
      setAccount(null);
      setUnit(null);
      setError('Logout failed. Please clear your browser cache.');
      authService.setStoredUserInfo(null);
    }
  };

  /**
   * Update the user's profile
   * @param {object} profileData - Profile data to update
   * @returns {Promise<object>} Updated profile
   */
  const updateProfile = async (profileData) => {
    setError(null);
    setLoading(true);
    
    try {
      const updatedProfile = await authService.updateUserProfile(profileData);
      setUser(updatedProfile);
      setLoading(false);
      return updatedProfile;
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Profile update failed.');
      setLoading(false);
      throw err;
    }
  };

  /**
   * Change the user's password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<object>} Result of password change
   */
  const changePassword = async (currentPassword, newPassword) => {
    setError(null);
    setLoading(true);
    
    try {
      const result = await authService.changePassword(currentPassword, newPassword);
      setLoading(false);
      return result;
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Password change failed.');
      setLoading(false);
      throw err;
    }
  };

  /**
   * Check if user has a specific role
   * @param {string} roleName - Role to check
   * @returns {boolean} True if user has the role
   */
  const hasRole = (roleName) => {
    if (!user || !user.roles) {
      return false;
    }
    return user.roles.includes(roleName);
  };

  /**
   * Manually refresh the access token
   * @returns {Promise<boolean>} Whether the refresh was successful
   */
  const refreshToken = async () => {
    setTokenRefreshInProgress(true);
    try {
      const result = await authService.refreshAccessToken();
      return result;
    } catch (err) {
      // console.error('Error refreshing token:', err);
      return false;
    } finally {
      setTokenRefreshInProgress(false);
    }
  };

  /**
   * Clear any error messages
   */
  const clearError = () => {
    setError(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        account,
        unit,
        loading,
        error,
        tokenRefreshInProgress,
        login,
        register,
        registerInternal,
        logout,
        updateProfile,
        changePassword,
        hasRole,
        clearError,
        refreshToken,
        isAuthenticated: () => !!user && isAuthChecked
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to use the auth context
 * @returns {object} Auth context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 